import _ from 'lodash'
import qs from 'querystring'
import request from '@/utils/request'
import { API_PREFIX } from '@/utils/constants'

const TYPE_MAP = {
  0: '项目',
  1: '企业',
}

/**
 * @api /api/v1.Project/defaultProjectList
 * @method GET
 * @description 获取可选默认进入组织的列表
 *
 */
export async function findAll() {
  try {
    const res = await request.get(`${API_PREFIX}/api/v1.Project/defaultProjectList`)
    return res.data
  } catch (e) {
    return []
  }
}

/**
 * @api /api/v1.Project/defaultProject
 * @method GET
 * @description 获取当前的默认组织
 *
 */
export async function getDefault() {
  try {
    const res = await request.get(`${API_PREFIX}/api/v1.Project/defaultProject`)
    return parseInt(res.data, 10) || null
  } catch (e) {
    return null
  }
}

/**
 * @api /api/v1.Project/setDefaultProject
 * @method GET
 * @description 设置默认进入组织
 *
 * @param {number} id 组织 ID
 *
 */
export async function changeDefaultProject(params) {
  try {
    const query = {
      id: params.id,
    }
    const qstr = qs.stringify(query)
    await request.get(`${API_PREFIX}/api/v1.Project/setDefaultProject?${qstr}`)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Project/projectList
 * @method GET
 * @description 获取组织列表
 *
 * @param {number} page 页码
 * @param {number} total 每页记录数
 * @param {string} search 组织名称搜索关键字
 * @param {number} project_type 组织类型，0:项目、1:企业
 *
 */
export async function find(params) {
  try {
    const query = {
      page: params.page,
      total: params.pageSize,
    }
    if (params.keyword) {
      query.search = params.keyword
    }
    if (_.isNumber(params.type)) {
      query.project_type = params.type
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Project/projectList?${qstr}`)
    const paginate = res.data.page
    const list = res.data.data
    return {
      page: paginate.current_page,
      pageSize: paginate.per_page,
      total: paginate.total,
      totalPages: paginate.last_page,
      results: list.map((item, index) => ({
        index: (paginate.current_page - 1) * paginate.per_page + index + 1,
        id: item.id,
        name: item.name,
        type: TYPE_MAP[item.type],
        category: item.classification,
        nature: item.cate,
        address: item.place,
        staffCount: item.staff_count,
        enabled: !item.stop,
      })),
    }
  } catch (e) {
    return {
      page: params.page,
      pageSize: params.pageSize,
      total: 0,
      totalPages: 1,
      results: [],
    }
  }
}

/**
 * @api /api/v1.Project/stop
 * @method GET
 * @description 启用/停用组织
 *
 * @param {number} id 组织 ID
 * @param {number} stop 0:启用、1:停用
 *
 */
export async function toggleEnabled(params) {
  try {
    const query = {
      id: params.id,
      stop: params.enabled ? 0 : 1,
    }
    const qstr = qs.stringify(query)
    await request.post(`${API_PREFIX}/api/v1.Project/stop?${qstr}`)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Project/delProjects
 * @method POST
 * @description 批量删除组织
 *
 * @param {number[]} ids 组织 ID 数组
 *
 */
export async function remove(params) {
  try {
    const query = {
      ids: params.ids,
    }
    await request.post(`${API_PREFIX}/api/v1.Project/delProjects`, query)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Project/getPlaceName
 * @method GET
 * @description 获取联动地址
 *
 * @param {number} place_id
 *
 */
export async function getAddresses(params) {
  try {
    const query = {
      place_id: params.id,
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Project/getPlaceName?${qstr}`)
    return res.data
  } catch (e) {
    return []
  }
}

/**
 * @api /api/v1.Project/createProjectQRcode
 * @method GET
 * @description 生成项目的二维码
 *
 * @param {number} id 组织 ID
 *
 */
export async function generateQrcode(params) {
  try {
    const query = {
      id: params.id,
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Project/createProjectQRcode?${qstr}`)
    return res.data.url
  } catch (e) {
    return null
  }
}

/**
 * @api /api/v1.Project/addProject
 * @method POST
 * @description 创建组织
 *
 * @param {string} name 名称
 * @param {string} project_unit_name 单位名称
 * @param {number} cate 单位性质 ID
 * @param {number} type 组织类型 ID，0: 项目; 1: 企业
 * @param {number} classification 所属行业
 * @param {string} identifier 标识码
 * @param {number} province_id 省份 ID
 * @param {number} city_id 城市 ID
 * @param {number} town_id 县/区 ID
 * @param {string} area 详细地址
 * @param {string} description 备注
 * @param {number} set_belong 是否切换项目 1:切换; 2: 不切换
 *
 */
export async function create(params) {
  try {
    const query = {
      name: params.name,
      project_unit_name: params.unitName,
      cate: params.nature,
      type: 0, // 固定为“项目”
      classification: 1, // 固定为“房建”
      set_belong: params.setBelong ? 1 : 2,
    }
    if (params.identifier) {
      query.identifier = params.identifier
    }
    const address = params.address || {}
    if (address.province) {
      query.province_id = address.province
    } else {
      query.province_id = 0
    }
    if (address.city) {
      query.city_id = address.city
    } else {
      query.city_id = 0
    }
    if (address.area) {
      query.town_id = address.area
    } else {
      query.town_id = 0
    }
    if (address.detail) {
      query.area = address.detail
    } else {
      query.area = ''
    }
    await request.post(`${API_PREFIX}/api/v1.Project/addProject`, query)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Project/projectDetail
 * @method GET
 * @description 获取组织详情
 *
 * @param {number} id 组织 ID
 *
 */
export async function get(params) {
  try {
    const query = {
      id: params.id,
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Project/projectDetail?${qstr}`)
    const data = res.data
    return {
      id: data.id,
      type: TYPE_MAP[data.type] || '',
      category: data.classificationInfo || '',
      nature: data.cateInfo || '',
      name: data.name,
      address: {
        province: data.province_id || null,
        city: data.city_id || null,
        area: data.town_id || null,
        detail: data.area || '',
      },
      note: data.description,
    }
  } catch (e) {
    return null
  }
}

/**
 * @api /api/v1.Project/editProject
 * @method POST
 * @description 编辑组织
 *
 * @param {number} id 组织 ID
 * @param {string} name 名称
 * @param {number} cate 单位性质 ID
 * @param {number} type 组织类型 ID，0: 项目; 1: 企业
 * @param {number} classification 所属行业
 * @param {number} province_id 省份 ID
 * @param {number} city_id 城市 ID
 * @param {number} town_id 县/区 ID
 * @param {string} area 详细地址
 * @param {string} description 备注
 *
 */
export async function update(params) {
  try {
    const query = {
      id: params.id,
      name: params.name,
      description: params.note,
    }
    const address = params.address
    if (address.province) {
      query.province_id = address.province
    } else {
      query.province_id = 0
    }
    if (address.city) {
      query.city_id = address.city
    } else {
      query.city_id = 0
    }
    if (address.area) {
      query.town_id = address.area
    } else {
      query.town_id = 0
    }
    if (address.detail) {
      query.area = address.detail
    } else {
      query.area = ''
    }
    await request.post(`${API_PREFIX}/api/v1.Project/editProject`, query)
    return true
  } catch (e) {
    return false
  }
}

// 暂时写死，以后可能会从数据库中取
export async function findProjectCategories() {
  try {
    const data = [{
      id: 1,
      name: '房建',
      // }, {
      //   value: 2,
      //   label: '其他',
    }]
    return data
  } catch (e) {
    return []
  }
}

/**
 * @api /api/v1.Project/projectTypeList
 * @method GET
 * @description 获取单位性质列表
 *
 */
export async function findProjectNatures() {
  try {
    const res = await request.get(`${API_PREFIX}/api/v1.Project/projectTypeList`)
    return res.data.map(item => ({
      id: item.id,
      name: item.project_type,
    }))
  } catch (e) {
    return []
  }
}
